<template>
  <div
    :id="name"
    @click="toggleSelect"
    @blur="isOpen = false"
    :class="['select', selectClass, { active: isOpen, disabled }]"
    data-testid="selectInput"
  >
    <div class="select__current">
      <label class="select__current-label">{{ placeholder }}</label>
      <span>{{ currentText }}</span>
      <img src="@/assets/img/arrow-down.svg" alt="Seta para expandir select" class="select__current-arrow" />
    </div>
    <div class="select__list">
      <ul class="select__list-inner">
        <li v-for="(option, i) in options" :key="option.value" @click="select(i)" class="select__list-inner--option">
          {{ option.text }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, toRefs, watch, watchEffect } from 'vue';
import { useField } from 'vee-validate';

const props = defineProps({
  name: { type: String, required: true },
  options: { type: Array, required: true },
  initialOption: {
    type: String,
  },
  disabled: { type: Boolean, default: false },
  attrs: { type: Object, default: () => {} },
  externalChange: { type: Boolean, default: false },
});
const { name, options, initialOption, disabled, attrs } = toRefs(props);

const emit = defineEmits(['updateSelectValue']);

const currentText = ref('');
const isOpen = ref(false);
const selectClass = computed(() => (attrs.value?.colorMode === 'light' ? 'grp-input-light' : 'grp-input-main'));
const index = computed(() => {
  if (initialOption.value === 'first') {
    return 0;
  }

  if (!isNaN(initialOption.value)) {
    return Number(initialOption.value) - 1;
  }

  return options.value.length - 1;
});
const placeholder = computed(() => (attrs.value?.placeholder ? attrs.value.placeholder : 'Número de parcelas'));

const { value } = options.value.length ? options.value[index.value] : { text: 'Número de parcelas', value: '' };
const { value: currentValue } = useField(name, null, { initialValue: value });

watchEffect(() => {
  if (disabled.value && isOpen.value) {
    isOpen.value = false;
  }
});

watchEffect(() => {
  if (options.value.length) {
    currentText.value = options.value[index.value].text;
  }
});

watch(currentValue, (newValue) => {
  if (props.externalChange) {
    const index = props.options.findIndex((option) => option.value === newValue);
    select(index);
  }
});

function select(index) {
  if (disabled.value) {
    return;
  }

  const selectedOption = options.value[index];
  currentText.value = selectedOption.text;
  currentValue.value = selectedOption.value;

  emit('updateSelectValue', selectedOption);
}

function toggleSelect() {
  if (disabled.value) {
    return;
  }

  isOpen.value = !isOpen.value;
}
</script>

<style lang="scss" scoped>
.select {
  position: relative;
  cursor: pointer;
}

.select__current {
  width: 100%;
  min-height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;

  &-label {
    display: none;
  }

  &-arrow {
    transition-property: transform;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 300ms;
    margin-right: 8px;
  }
}

.grp-input-light .select__current {
  > label {
    display: block;
    position: absolute;
    color: $dark-200;
    top: 2px;
    left: 16px;
    font-size: 0.625rem;
  }

  > span {
    color: $dark-600;
  }
}

.select__list {
  width: 100%;
  max-height: 208px;
  position: absolute;
  top: 48px;
  right: 0;
  padding: 0;
  padding-top: 8px;
  padding-bottom: 8px;
  overflow-y: auto;
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
  opacity: 0;
  pointer-events: none;
  border-bottom-right-radius: 6px;
  border-bottom-left-radius: 6px;
  z-index: 50;

  // TODO: review
  background-color: $dark-700;

  &-inner {
    scrollbar-color: transparent transparent;

    &--option {
      padding: 12px 20px;

      &:hover {
        // TODO: review
        background-color: $dark-600;
      }
    }
  }
}

.grp-input-light .select__list .select__list-inner .select__list-inner--option:hover {
  background-color: $dark-50;
}

/* Active classes */
.select.active .select__list {
  opacity: 1;
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
  pointer-events: all;
}

.select.active.grp-input-light .select__list {
  background-color: $dark-30;
  border-top: 1px solid $dark-10;

  > ul > li {
    color: $dark-600;
  }
}

.select.active .select__current-arrow {
  transform: rotate(180deg);
}

/* Disabled classes */
.select.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.select.disabled .select__current-arrow {
  opacity: 0.5;
}

.select.disabled .select__current > span {
  color: $dark-300;
}

.select__list-inner::-webkit-scrollbar {
  width: 4px;
  border-radius: 24px;

  // TODO: review
  background-color: $dark-600;
}

.select__list-inner::-webkit-scrollbar-thumb {
  background-color: $spirit-stone;
  border-radius: 12px;
}

.select {
  &.custom {
    .select__list {
      box-shadow: inset 0px -12px 10px -8px $dark-400;
      border: 1px solid $dark-700;

      &-inner {
        &--option {
          padding: 10px 20px;
          position: relative;

          &::after {
            content: '';
            display: block;
            height: 1px;
            width: 90%;
            background: $dark-400;
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            margin: auto;
            opacity: 0.5;
          }

          &:last-child {
            &::after {
              display: none;
            }
          }
        }
      }
    }
    &.active {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
}
</style>
